import { useState } from "react";
import axiosClient from "../api/axiosClient";
import { toast } from "react-toastify";
import "./admin.css";
import "./AddBike.css";

const EMPTY_FORM = {
  name: "",
  type: "",
  pricePerDay: "",
  hourlyPrice: "",
  weekendPrice: "",
  engineCapacity: "",
  weight: "",
  mileage: "",
  fuelTankCapacity: "",
  totalUnits: "1",
  image: null,
};

function AddBike() {
  const [bike, setBike] = useState(EMPTY_FORM);
  const [colorInput, setColorInput] = useState("#000000");
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleFileChange = (e) => setBike({ ...bike, image: e.target.files[0] });

  const addColor = () => {
    if (!colors.includes(colorInput)) {
      setColors([...colors, colorInput]);
    }
  };

  const removeColor = (c) => setColors(colors.filter((x) => x !== c));

  const submit = (e) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    formData.append("name", bike.name);
    formData.append("type", bike.type);
    formData.append("pricePerDay", bike.pricePerDay);
    if (bike.hourlyPrice)      formData.append("hourlyPrice", bike.hourlyPrice);
    if (bike.weekendPrice)     formData.append("weekendPrice", bike.weekendPrice);
    if (bike.engineCapacity)   formData.append("engineCapacity", bike.engineCapacity);
    if (bike.weight)           formData.append("weight", bike.weight);
    if (bike.mileage)          formData.append("mileage", bike.mileage);
    if (bike.fuelTankCapacity) formData.append("fuelTankCapacity", bike.fuelTankCapacity);
    if (bike.totalUnits)       formData.append("totalUnits", bike.totalUnits);
    colors.forEach((c) => formData.append("colors[]", c));
    formData.append("image", bike.image);

    axiosClient
      .post("/admin/bikes", formData, { headers: { "Content-Type": "multipart/form-data" } })
      .then(() => {
        toast.success("Bike added successfully!");
        setBike(EMPTY_FORM);
        setColors([]);
      })
      .catch(() => toast.error("Failed to add bike. Please try again."))
      .finally(() => setLoading(false));
  };

  return (
    <>
      {loading && (
        <div className="admin-submit-overlay">
          <div className="admin-spinner-ring" />
          <p className="admin-spinner-label">Adding bike to fleet…</p>
        </div>
      )}

      <div className="admin-page-header">
        <h1>Add Bike</h1>
        <p>List a new bike in the rental fleet</p>
      </div>

      <div className="add-bike-container">
        <div className="add-bike-form-card">
          <form onSubmit={submit}>
            <div className="add-bike-grid">

              {/* ── Core Details ── */}
              <div className="add-bike-section-title full-width">🏍️ Core Details</div>

              <div className="admin-form-group">
                <label>Bike Name <span className="req">*</span></label>
                <input
                  className="admin-input"
                  value={bike.name}
                  placeholder="e.g. Honda Activa 6G"
                  onChange={(e) => setBike({ ...bike, name: e.target.value })}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Type <span className="req">*</span></label>
                <input
                  className="admin-input"
                  value={bike.type}
                  placeholder="e.g. Scooter, Cruiser, Sports"
                  onChange={(e) => setBike({ ...bike, type: e.target.value })}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Total Units Available</label>
                <input
                  className="admin-input"
                  type="number"
                  min="1"
                  value={bike.totalUnits}
                  placeholder="e.g. 3"
                  onChange={(e) => setBike({ ...bike, totalUnits: e.target.value })}
                />
              </div>

              {/* ── Pricing ── */}
              <div className="add-bike-section-title full-width">💰 Pricing</div>

              <div className="admin-form-group">
                <label>Daily Price (₹) <span className="req">*</span></label>
                <input
                  className="admin-input"
                  type="number"
                  placeholder="e.g. 300"
                  value={bike.pricePerDay}
                  onChange={(e) => setBike({ ...bike, pricePerDay: e.target.value })}
                  required
                />
              </div>

              <div className="admin-form-group">
                <label>Hourly Price (₹)</label>
                <input
                  className="admin-input"
                  type="number"
                  placeholder="e.g. 50"
                  value={bike.hourlyPrice}
                  onChange={(e) => setBike({ ...bike, hourlyPrice: e.target.value })}
                />
              </div>

              <div className="admin-form-group">
                <label>Weekend Price (₹)</label>
                <input
                  className="admin-input"
                  type="number"
                  placeholder="e.g. 350"
                  value={bike.weekendPrice}
                  onChange={(e) => setBike({ ...bike, weekendPrice: e.target.value })}
                />
              </div>

              {/* ── Specs ── */}
              <div className="add-bike-section-title full-width">🔧 Specifications</div>

              <div className="admin-form-group">
                <label>Engine Capacity</label>
                <input
                  className="admin-input"
                  value={bike.engineCapacity}
                  placeholder="e.g. 110 CC"
                  onChange={(e) => setBike({ ...bike, engineCapacity: e.target.value })}
                />
              </div>

              <div className="admin-form-group">
                <label>Weight</label>
                <input
                  className="admin-input"
                  value={bike.weight}
                  placeholder="e.g. 102 Kgs"
                  onChange={(e) => setBike({ ...bike, weight: e.target.value })}
                />
              </div>

              <div className="admin-form-group">
                <label>Mileage</label>
                <input
                  className="admin-input"
                  value={bike.mileage}
                  placeholder="e.g. 60 Kms"
                  onChange={(e) => setBike({ ...bike, mileage: e.target.value })}
                />
              </div>

              <div className="admin-form-group">
                <label>Fuel Tank Capacity</label>
                <input
                  className="admin-input"
                  value={bike.fuelTankCapacity}
                  placeholder="e.g. 3.5 Litres"
                  onChange={(e) => setBike({ ...bike, fuelTankCapacity: e.target.value })}
                />
              </div>

              {/* ── Colors ── */}
              <div className="add-bike-section-title full-width">🎨 Available Colours</div>

              <div className="admin-form-group full-width color-picker-group">
                <div className="color-picker-row">
                  <input
                    type="color"
                    value={colorInput}
                    onChange={(e) => setColorInput(e.target.value)}
                    className="admin-color-input"
                    title="Pick a colour"
                  />
                  <input
                    type="text"
                    className="admin-input"
                    value={colorInput}
                    onChange={(e) => setColorInput(e.target.value)}
                    placeholder="#000000"
                    style={{ maxWidth: 120 }}
                  />
                  <button type="button" className="btn btn-ghost add-color-btn" onClick={addColor}>
                    + Add
                  </button>
                </div>
                {colors.length > 0 && (
                  <div className="color-swatch-list">
                    {colors.map((c) => (
                      <div key={c} className="color-swatch-tag">
                        <span className="swatch-dot" style={{ background: c }} />
                        <span className="swatch-hex">{c}</span>
                        <button type="button" className="swatch-remove" onClick={() => removeColor(c)}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Image ── */}
              <div className="add-bike-section-title full-width">📸 Bike Image</div>

              <div className="admin-form-group full-width">
                <label className="file-upload-wrapper">
                  <div className="file-upload-icon">📸</div>
                  <div className="file-upload-text">Click to upload image</div>
                  <div className="file-upload-hint">SVG, PNG, JPG or GIF (max. 5MB)</div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    required
                  />
                </label>
                {bike.image && (
                  <div className="image-preview">
                    <img src={URL.createObjectURL(bike.image)} alt="Preview" />
                  </div>
                )}
              </div>

            </div>

            <button
              type="submit"
              className="btn btn-primary submit-btn"
              disabled={loading}
            >
              {loading ? "Adding…" : "Add Bike to Fleet"}
            </button>
          </form>
        </div>

        <div className="add-bike-info-card">
          <h3>Tips for adding a bike</h3>
          <ul>
            <li>Use high-quality, well-lit images for better visibility.</li>
            <li>Fill in all specs so customers can see engine & mileage details.</li>
            <li>Set hourly & weekend prices to attract more bookings.</li>
            <li>Add colour swatches to help customers pick their preference.</li>
            <li>Ensure pricing is competitive based on the local market.</li>
          </ul>
        </div>
      </div>
    </>
  );
}

export default AddBike;
